import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getLastWorkspace } from '@/hooks/useWorkspaceContext';

/**
 * Shared "beautiful AI output" surface (NUMBER_ONE_PLAN W2.1). Single source
 * of truth so every AI surface — live panels AND saved history AND public
 * shared snapshots — renders identically: GFM tables with borders, styled
 * headings/blockquotes/lists/code, structured shapes (keigo subject/body,
 * deal-coach score+actions, QA/summary), JSON recovery, and linkable
 * citation pills. No raw markdown or JSON.stringify ever leaks to a user.
 */
export function MarkdownText({ text }: { text: string }) {
  return (
    <div className="text-ink-900 leading-relaxed space-y-3 [&_p]:m-0 [&_strong]:font-semibold [&_strong]:text-ink-900 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-1 [&_li]:marker:text-ink-400 [&_blockquote]:border-l-2 [&_blockquote]:border-accent-300 [&_blockquote]:pl-3 [&_blockquote]:text-ink-600 [&_blockquote]:italic [&_hr]:border-ink-200 [&_hr]:my-4 [&_code]:bg-ink-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-[12px] [&_code]:font-mono [&_pre]:bg-ink-50 [&_pre]:border [&_pre]:border-ink-200 [&_pre]:rounded-md [&_pre]:px-3 [&_pre]:py-2 [&_pre]:overflow-x-auto [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_a]:text-accent-600 [&_a]:underline [&_h1]:text-base [&_h1]:font-semibold [&_h2]:text-sm [&_h2]:font-semibold [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-ink-700 [&_table]:w-full [&_table]:border-collapse [&_table]:text-[13px] [&_th]:border [&_th]:border-ink-200 [&_th]:px-2 [&_th]:py-1 [&_th]:bg-ink-50 [&_th]:text-left [&_td]:border [&_td]:border-ink-200 [&_td]:px-2 [&_td]:py-1">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
    </div>
  );
}

/**
 * Many agents prompt the model for JSON like `{ "subject": "...", ... }`.
 * The run() pipeline returns raw text, often a stringified JSON — recover a
 * structured object before falling through to markdown.
 */
export function tryParseStructured(s: string): unknown | null {
  const stripped = s
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/```\s*$/, '')
    .trim();
  const match = stripped.match(/^\{[\s\S]*\}$/);
  if (!match) return null;
  try {
    return JSON.parse(match[0]);
  } catch {
    return null;
  }
}

/** Render any AI output to a beautifully-formatted node. */
export function renderAiOutput(output: unknown): React.ReactNode {
  if (typeof output === 'string') {
    const parsed = tryParseStructured(output);
    if (parsed && typeof parsed === 'object') return renderAiOutput(parsed);
    return <MarkdownText text={output} />;
  }

  if (output && typeof output === 'object') {
    const o = output as Record<string, unknown>;

    if (typeof o.answer === 'string') return <MarkdownText text={o.answer} />;
    if (typeof o.summary === 'string') return <MarkdownText text={o.summary} />;

    if (typeof o.subject === 'string' && typeof o.body === 'string') {
      return (
        <div className="space-y-3">
          <p className="font-medium text-ink-900">
            <span className="text-ink-500 text-xs mr-2">件名:</span>
            {o.subject}
          </p>
          <MarkdownText text={o.body} />
        </div>
      );
    }

    if (typeof o.rationale === 'string' && Array.isArray(o.nextBestActions)) {
      return (
        <div className="space-y-3">
          {typeof o.healthScore === 'number' && (
            <div className="flex items-baseline gap-2">
              <span className="num text-3xl font-medium tabular-nums text-ink-900">
                {o.healthScore}
              </span>
              <span className="text-[11px] uppercase tracking-[0.16em] text-ink-500">/10</span>
            </div>
          )}
          <MarkdownText text={o.rationale} />
          <ul className="space-y-1.5 text-ink-900">
            {(o.nextBestActions as string[]).map((a, i) => (
              <li key={i} className="flex gap-2">
                <span className="num text-ink-400">{i + 1}.</span>
                <span>{a}</span>
              </li>
            ))}
          </ul>
        </div>
      );
    }
  }

  // Last resort — pretty JSON (still styled, never a raw dump).
  return (
    <pre className="text-xs whitespace-pre-wrap text-ink-700 font-mono overflow-x-auto bg-ink-50 border border-ink-200 rounded-md px-3 py-2">
      {JSON.stringify(output, null, 2)}
    </pre>
  );
}

/**
 * A citation pill that LINKS to the cited record's tool (W2.1: citations were
 * dead-end chips). account/contact/deal/signal/thread deep-link into the
 * workspace so the AI's evidence is navigable, closing the connectedness gap.
 */
const CITATION_TINT: Record<string, string> = {
  contact: 'bg-amber-50 text-amber-700 hover:bg-amber-100',
  account: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
  signal: 'bg-rose-50 text-rose-700 hover:bg-rose-100',
  deal: 'bg-sky-50 text-sky-700 hover:bg-sky-100',
  thread: 'bg-purple-50 text-purple-700 hover:bg-purple-100',
};
const CITATION_ROUTE: Record<string, string> = {
  contact: 'people',
  account: 'companies',
  signal: 'feed',
  deal: 'deals',
  thread: 'inbox',
};

export function Citation({ kind, id, wsId }: { kind: string; id: string; wsId?: string }) {
  const { t } = useTranslation();
  const short = id.length > 8 ? id.slice(0, 8) : id;
  const tint = CITATION_TINT[kind] || 'bg-ink-100 text-ink-700 hover:bg-ink-200';
  const ws = wsId || getLastWorkspace();
  const route = CITATION_ROUTE[kind];
  const cls = `inline-flex items-center gap-1 px-2 py-1 rounded text-[10.5px] font-semibold tracking-[0.12em] uppercase transition-colors ${tint}`;
  const inner = (
    <>
      {kind} <span className="num opacity-70">#{short}</span>
    </>
  );
  if (ws && route) {
    return (
      <Link to={`/app/workspace/${ws}/${route}?focus=${id}`} className={cls} title={t('agents.citation_full_id', { id })}>
        {inner}
      </Link>
    );
  }
  return (
    <span className={cls} title={t('agents.citation_full_id', { id })}>
      {inner}
    </span>
  );
}
